import styled, { css } from "styled-components"

interface SectionProps {
  $hideOnMobile?: boolean
  $hideOnDesktop?: boolean
}

export const Section = styled.section<SectionProps>`
  width: 100%;
  position: relative;
  /* Sem overflow:hidden — os dropdowns (Modelo/Versão) precisam ultrapassar os
     limites da seção sem serem recortados. A imagem de fundo já fica contida por
     ser position:absolute + inset:0 + 100%/100% (não depende deste overflow). */
  overflow: visible;
  background-color: var(--general-background, #f8fafc);
  color: var(--text-body, #64748b);

  ${({ $hideOnMobile }) =>
    $hideOnMobile &&
    css`
      @media (max-width: 767px) {
        display: none;
      }
    `}
  ${({ $hideOnDesktop }) =>
    $hideOnDesktop &&
    css`
      @media (min-width: 768px) {
        display: none;
      }
    `}
`

interface ContainerProps {
  $format?: "vitrine" | "stepByStep"
  $padding?: {
    typeMeasure?: string
    desktop?: { paddingTop?: number; paddingBottom?: number }
    mobile?: { paddingTop?: number; paddingBottom?: number }
  }
}

// Imagem de plano de fundo: renderizada como <img> (carrega alt) atrás do conteúdo.
export const BackgroundImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
`

export const Container = styled.div<ContainerProps>`
  max-width: 1216px;
  margin: 0 auto;
  padding: 3rem 1rem;
  position: relative;
  z-index: 1;
  display: grid;
  gap: 2rem;
  /* Mobile: empilhado. No passo a passo o card (stepper+carro+form) é uma linha só. */
  grid-template-areas: ${({ $format }) =>
    $format === "stepByStep"
      ? '"intro" "card"'
      : '"intro" "preview" "form"'};

  ${({ $padding }) => {
    const unit = $padding?.typeMeasure || "px"
    const m = $padding?.mobile
    return (
      m &&
      css`
        @media (max-width: 767px) {
          ${m.paddingTop != null &&
          css`
            padding-top: ${m.paddingTop}${unit};
          `}
          ${m.paddingBottom != null &&
          css`
            padding-bottom: ${m.paddingBottom}${unit};
          `}
        }
      `
    )
  }}

  ${({ $padding }) => {
    const unit = $padding?.typeMeasure || "px"
    const d = $padding?.desktop
    return (
      d &&
      css`
        @media (min-width: 768px) {
          ${d.paddingTop != null &&
          css`
            padding-top: ${d.paddingTop}${unit};
          `}
          ${d.paddingBottom != null &&
          css`
            padding-bottom: ${d.paddingBottom}${unit};
          `}
        }
      `
    )
  }}

  @media (min-width: 768px) {
    align-items: start;

    ${({ $format }) =>
      $format === "stepByStep"
        ? css`
            /* Passo a passo: título fora; o card único (StepCard) ocupa a linha toda. */
            grid-template-columns: 1fr;
            grid-template-areas: "intro" "card";
          `
        : css`
            /* Vitrine: veículo à esquerda (cabeçalho + preview), formulário à direita. */
            grid-template-columns: 1fr minmax(360px, 460px);
            grid-template-areas: "intro form" "preview form";
          `}
  }
`

// Passo a passo: card único envolvendo stepper + veículo + formulário (o design).
// O título/subtítulo (Intro) fica fora, acima. No vitrine este card não é usado.
export const StepCard = styled.div`
  grid-area: card;
  display: grid;
  gap: 1.5rem;
  background: var(--cards-background, #ffffff);
  border: 1px solid var(--cards-border, #b7c0cd);
  border-radius: 16px;
  padding: 1.5rem;
  /* Mobile: campos primeiro, preview do veículo abaixo. */
  grid-template-areas: "stepper" "form" "preview";

  @media (min-width: 768px) {
    align-items: start;
    grid-template-columns: minmax(360px, 460px) 1fr;
    grid-template-areas: "stepper stepper" "form preview";
    padding: 2rem;
  }
`

// Confirmação ocupa a largura toda (o próprio componente monta as 2 colunas).
export const ConfirmationContainer = styled.div`
  max-width: 1216px;
  margin: 0 auto;
  padding: 3rem 1rem;
  position: relative;
  z-index: 1;
`

export const Intro = styled.div<{ $align?: "esquerda" | "centro" }>`
  grid-area: intro;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  ${({ $align }) =>
    $align === "centro" &&
    css`
      text-align: center;
      align-items: center;
    `}
`

// Passo a passo: o stepper ocupa a linha toda acima do formulário e do veículo.
export const StepperArea = styled.div`
  grid-area: stepper;
`

export const Title = styled.h2`
  font-family: var(--typography-heading-font-family);
  font-weight: var(--typography-heading-font-weight, 700);
  font-size: var(--typography-heading-font-size-desktop, 2rem);
  color: var(--td-block-title, var(--text-title, #0f172a));
  margin: 0;

  @media (max-width: 767px) {
    font-size: var(--typography-heading-font-size-mobile, 1.5rem);
  }
`

export const Description = styled.p`
  color: var(--td-block-desc, var(--text-body, #64748b));
  margin: 0;
`

export const Card = styled.div<{ $format?: "vitrine" | "stepByStep" }>`
  grid-area: form;
  ${({ $format }) =>
    $format === "stepByStep"
      ? css`
          background: transparent;
          border: none;
          padding: 0;
        `
      : css`
          background: var(--cards-background, #ffffff);
          border: 1px solid var(--cards-border, #b7c0cd);
          border-radius: 12px;
          padding: 1.25rem;
        `}
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

export const Stepper = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 0.75rem;
`

export const StepperMeta = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-body, #64748b);
  white-space: nowrap;
`

export const StepperTrack = styled.div`
  display: flex;
  gap: 4px;
`

type StepState = "current" | "completed" | "pending"
export const StepperSegment = styled.span<{ $state: StepState }>`
  flex: 1;
  height: 3px;
  border-radius: 3px;
  background: ${({ $state }) =>
    $state === "current"
      ? "var(--td-step-current, var(--buttons-background, var(--general-primary, #0027d4)))"
      : $state === "completed"
        ? "var(--td-step-completed, var(--buttons-background, var(--general-primary, #0027d4)))"
        : "var(--td-step-pending, var(--general-border, #e2e8f0))"};
`

export const StepperName = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-body, #64748b);
  white-space: nowrap;
`

export const StepHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

export const StepHeaderTitle = styled.h3`
  font-family: var(--typography-heading-font-family);
  font-size: 1.125rem;
  font-weight: var(--typography-heading-font-weight, 700);
  color: var(--td-step-title, var(--text-title, #0f172a));
  margin: 0;
`

export const StepHeaderSubtitle = styled.p`
  font-size: 0.875rem;
  color: var(--td-step-subtitle, var(--text-body, #64748b));
  margin: 0;
`

export const StepBody = styled.div`
  min-height: 120px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  color: var(--text-body, #64748b);
`

export const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`

export const FieldLabel = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--td-field-label, var(--text-title, #0f172a));
`

export const PreviewPane = styled.div<{ $hideOnMobile?: boolean }>`
  grid-area: preview;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;

  ${({ $hideOnMobile }) =>
    $hideOnMobile &&
    css`
      @media (max-width: 767px) {
        display: none;
      }
    `}
`

/* Card opcional (aba Layout → Container do preview): envolve SÓ a mídia do veículo
   — o nome/versão e a legenda ficam ABAIXO, fora do card (como o design). */
export const PreviewCard = styled.div<{
  $container?: boolean
  $padding?: { desktop?: number; mobile?: number }
}>`
  width: 100%;
  ${({ $container }) =>
    $container &&
    css`
      background: var(--cards-background, #ffffff);
      border: 1px solid var(--cards-border, #b7c0cd);
      border-radius: 12px;
      padding: 1rem;
    `}
  ${({ $container, $padding }) =>
    $container &&
    $padding?.mobile != null &&
    css`
      @media (max-width: 767px) {
        padding: ${$padding.mobile}px;
      }
    `}
  ${({ $container, $padding }) =>
    $container &&
    $padding?.desktop != null &&
    css`
      @media (min-width: 768px) {
        padding: ${$padding.desktop}px;
      }
    `}
`

export const PreviewPlaceholder = styled.div`
  width: 100%;
  text-align: center;
  padding: 0.5rem 1rem;
  color: var(--text-body, #94a3b8);
`

/* Empilha o veículo e, abaixo, a legenda "Arraste para girar" (o DragHint que o
   VehicleMedia renderiza como irmão do viewer). O carro tem altura própria fixa
   (14/16rem), então NÃO usar aspect-ratio + overflow:hidden aqui: recortava o hint. */
export const PreviewMedia = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`

export const PreviewImage = styled.img`
  width: 100%;
  object-fit: contain;
`

export const VehicleName = styled.strong`
  font-size: 1.125rem;
  color: var(--td-step-title, var(--text-title, #0f172a));
`

export const VehicleVersion = styled.span`
  color: var(--td-step-subtitle, var(--text-body, #64748b));
`

export const DayLabel = styled.strong`
  font-size: 0.9375rem;
  color: var(--td-field-label, var(--text-title, #0f172a));
`

export const ChipsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;

  @media (max-width: 767px) {
    grid-template-columns: repeat(3, 1fr);
  }
`

export const Chip = styled.button<{ $selected?: boolean }>`
  height: 38px;
  border-radius: 8px;
  border: 1px solid
    ${({ $selected }) => ($selected ? "var(--general-primary, #0027d4)" : "var(--general-border, #b7c0cd)")};
  background: ${({ $selected }) =>
    $selected
      ? "var(--general-primary, #0027d4)"
      : "var(--td-input-bg, var(--cards-background, #fff))"};
  color: ${({ $selected }) =>
    $selected ? "var(--buttons-text, #fff)" : "var(--text-title, #0f172a)"};
  font-size: 0.875rem;
  cursor: pointer;
`

export const Duration = styled.span`
  font-size: 0.8125rem;
  color: var(--text-body, #64748b);
`

export const InfoBox = styled.div`
  display: flex;
  gap: 0.5rem;
  padding: 0.75rem;
  border-radius: 8px;
  background: var(--general-background, #f1f5f9);
  font-size: 0.8125rem;
  color: var(--td-notice, var(--text-body, #64748b));
`

export const EmptySlots = styled.span`
  font-size: 0.875rem;
  color: var(--text-body, #64748b);
`

export const UnavailableNotice = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: 0.875rem 1rem;
  border-radius: 8px;
  border-left: 3px solid var(--general-primary, #0027d4);
  background: var(--general-background, #f1f5f9);
`

export const UnavailableTitle = styled.strong`
  font-weight: 700;
  font-size: 0.9375rem;
  color: var(--td-block-title, var(--text-title, #0f172a));
`

export const UnavailableBody = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: var(--td-notice, var(--text-body, #64748b));
`

export const SubmitError = styled.span`
  font-size: 0.875rem;
  color: var(--destructive, #dc2626);
`

export const Nav = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 767px) {
    button {
      flex: 1;
    }
  }
`

const buttonBase = css`
  height: 40px;
  padding: 0 1.25rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const PrimaryButton = styled.button`
  ${buttonBase}
  background: var(--buttons-background, #0027d4);
  color: var(--buttons-text, #ffffff);
  border: 1px solid var(--buttons-border, #0027d4);
`

export const GhostButton = styled.button`
  ${buttonBase}
  background: transparent;
  color: var(--td-back-text, var(--text-body, #64748b));
  border: 1px solid var(--general-border, #b7c0cd);
`
